import React, { forwardRef, SelectHTMLAttributes } from 'react';
import './Select.css';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /**
   * Select label
   */
  label?: string;

  /**
   * Select options
   */
  options: SelectOption[];

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  helperText?: string;

  /**
   * Size variant
   */
  size?: 'sm' | 'base' | 'lg';

  /**
   * Full width
   */
  fullWidth?: boolean;

  /**
   * Placeholder option
   */
  placeholder?: string;
}

/**
 * Select component
 *
 * Styled select dropdown with label, error states, and sizes
 *
 * @example
 * ```tsx
 * <Select
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[
 *     { label: 'United States', value: 'us' },
 *     { label: 'Canada', value: 'ca' },
 *     { label: 'Mexico', value: 'mx' },
 *   ]}
 *   value={country}
 *   onChange={(e) => setCountry(e.target.value)}
 * />
 * ```
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      error,
      helperText,
      size = 'base',
      fullWidth = false,
      placeholder,
      className = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const hasError = Boolean(error);

    return (
      <div className={`select-wrapper ${fullWidth ? 'select-wrapper--full-width' : ''}`}>
        {label && (
          <label className="select__label" htmlFor={props.id}>
            {label}
            {props.required && <span className="select__required">*</span>}
          </label>
        )}

        <div className="select__container">
          <select
            ref={ref}
            className={`select select--${size} ${hasError ? 'select--error' : ''} ${
              disabled ? 'select--disabled' : ''
            } ${className}`}
            disabled={disabled}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="select__icon">▼</div>
        </div>

        {(error || helperText) && (
          <div className={`select__helper ${hasError ? 'select__helper--error' : ''}`}>
            {error || helperText}
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
